import React, { useEffect, useState } from 'react';
import { Colors, Text, TextButton, UnderlinedTextInput } from 'library';
import { IconButton, Tooltip, styled, Grid } from '@mui/material';
import QuestionMark from '@mui/icons-material/QuestionMark';
import { BusinessData } from './BusinessModels';
import ProgramTypes from 'parent-portal/BusinessLocation/ProgramTypes';
import { consoleLogInDev } from 'shared';
import { getProgramTypes, IProgramType } from 'api/ProgramTypeApi';

export default function BusinessSignup({
    updateBusinessData,
    updateError,
    businessType,
    isValidProgramId,
    state,
    onNotBusinessClick,
}: {
    updateBusinessData: (data: BusinessData) => void;
    updateError: (error: boolean) => void;
    businessType: 'CHILD_CARE' | 'ELDERCARE';
    isValidProgramId: boolean;
    state?: string;
    onNotBusinessClick: () => void;
}) {
    const [name, setName] = useState<string>();
    const [programId, setProgramId] = useState<string>();
    const [nameError, setNameError] = useState<boolean>(false);
    const [programTypeOptions, setProgramTypeOptions] = useState<IProgramType[]>([]);
    const [programTypes, setProgramTypes] = useState<string[]>([]);

    function getProgramText() {
        if (businessType === 'ELDERCARE') {
            return 'Facility Id';
        }

        if (state === 'Florida') {
            return 'License Number';
        }

        return 'Provider ID/Program ID/License Number**';
    }

    const programText = getProgramText();

    useEffect(() => {
        setNameError(name === '');

        if (!!name && !nameError) {
            const data: BusinessData = {
                name,
                programId,
                programTypes: programTypeOptions.filter((x) => programTypes.includes(x.name)).map((x) => x.id),
            };
            updateBusinessData(data);
            updateError(false);
        } else {
            updateError(true);
        }
    }, [name, nameError, programId, programTypes]);

    useEffect(() => {
        if (!state) {
            if (programTypes.length) {
                setProgramTypes([]);
            }
        } else {
            getProgramTypes(state)
                .then((response) => {
                    setProgramTypeOptions(response);
                    if (!response.length) {
                        setProgramTypes([]);
                    }
                })
                .catch(consoleLogInDev);
        }
    }, [state]);

    function onProgramTypesChanges(option: string) {
        if (option in setProgramTypes) {
            setProgramTypes(programTypes.filter((x) => x !== option));
        } else {
            setProgramTypes([...programTypes, option]);
        }
    }

    function getProgramTypesSection() {
        if (!programTypeOptions.length) {
            return;
        }

        return (
            <ProgramTypeRow>
                <Text>Do you currently participate in any of the following state programs?</Text>
                <ProgramTypes
                    options={programTypeOptions.map((x) => x.name)}
                    programTypes={programTypes}
                    onProgramTypesChanged={(x) => onProgramTypesChanges(x)}
                />
            </ProgramTypeRow>
        );
    }

    return (
        <Grid container item xs={12}>
            <UnderlinedTextInput
                id="txtBusinessName"
                label="Business Name*"
                value={name ? name : ''}
                onChange={(value: string) => setName(value)}
                error={nameError}
                errorText={'Please enter a business name.'}
            />
            <TextButton buttonStyle={{ width: 'fit-content' }} onClick={onNotBusinessClick}>
                Not a Business? Click here.
            </TextButton>
            <ProgramRow>
                <Tooltip
                    placement="top"
                    title="Enter the Program Number associated with the address of the location you entered above."
                >
                    <Icon>
                        <Question />
                    </Icon>
                </Tooltip>
                <UnderlinedTextInput
                    id="txtProgramId"
                    label={programText}
                    value={programId ? programId : ''}
                    onChange={(value: string) => setProgramId(value)}
                    error={!isValidProgramId}
                    errorText="Please enter a valid program ID."
                />
            </ProgramRow>
            {getProgramTypesSection()}
        </Grid>
    );
}

const ProgramRow = styled('div')({
    display: 'flex',
    alignItems: 'flex-end',
    width: '100%',
});

const ProgramTypeRow = styled('div')({
    marginTop: 20,
});

const Question = styled(QuestionMark)({
    padding: 5,
});

const Icon = styled(IconButton)({
    color: Colors.darkGrey,
    backgroundColor: Colors.lightGrey,
    height: 20,
    width: 20,
    marginRight: 10,
    marginBottom: 2,
});
